import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Download, MousePointer2 } from "lucide-react";
import { RESUME_PDF, LINKS } from "../data/content";

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
    >
      {/* Parallax background layers */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#0c0e14_1px,transparent_1px),linear-gradient(to_bottom,#0c0e14_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,#000_30%,transparent_75%)]" />
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.75, 0.5] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 -left-20 w-[38rem] h-[38rem] rounded-full bg-[#2563eb]/25 blur-[130px]"
        />
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.35, 0.6, 0.35] }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5,
          }}
          className="absolute bottom-10 right-0 w-[30rem] h-[30rem] rounded-full bg-[#1d4ed8]/20 blur-[120px]"
        />
      </motion.div>

      <motion.div
        style={{ y: yContent, opacity }}
        className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-28"
      >
        {t.hero.status && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#2563eb] opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#2563eb]" />
              </span>
              <span className="font-mono text-[12px] tracking-[0.25em] uppercase text-[#8b95a5]">
                {t.hero.status}
              </span>
        </motion.div>
          )}

        <Line
          delay={0.25}
          className="font-mono text-[13px] md:text-sm tracking-[0.35em] uppercase text-[#2563eb] mb-5"
        >
          {t.hero.overline}
        </Line>

        <h1 className="font-display font-extrabold tracking-tighter leading-[0.9] text-[clamp(3rem,11vw,9.5rem)]">
          <Line delay={0.35}>{t.hero.name1}</Line>
          <Line delay={0.45} className="text-stroke">
            {t.hero.name2}
          </Line>
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md text-[#8b95a5] text-base md:text-lg leading-relaxed"
          >
            {t.hero.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex flex-wrap items-center gap-3"
          >
            <a
              data-testid="hero-cta-experience"
              href="#experience"
              className="group flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#2563eb] text-white font-medium hover:bg-[#1d4ed8] transition-colors duration-300 accent-glow"
            >
              {t.hero.cta1}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </a>
            <a
              data-testid="hero-cta-download"
              href={RESUME_PDF}
              download
              className="group flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-white font-medium hover:border-[#2563eb] hover:bg-white/[0.03] transition-colors duration-300"
            >
              <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-300" />
              {t.hero.cta2}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-6 md:left-10 flex items-center gap-2 text-[#8b95a5]"
      >
        <MousePointer2 className="w-3.5 h-3.5" />
        <span className="font-mono text-[11px] tracking-[0.2em] uppercase">
          {t.hero.scroll}
        </span>
      </motion.div>
    </section>
  );
};
